import type { MfaPolicy } from '@inou/shared';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { ApiError, api, type MfaUnenrolled } from '@/lib/api';

/**
 * 二段階認証の必須化。
 *
 * **有効にする前に、設定を求められる人数を出す。** 黙って切り替えると、
 * 第 2 要素を持っていない人は次のリクエストから業務 API が全部 403 になる
 * (登録画面には送られるので締め出されはしないが、何も知らされていないと
 * 事故に見える)。
 *
 * 数えているのは在籍中で TOTP も Passkey も持っていない人。Passkey を
 * 持っている人は Passkey でログインすれば関門に当たらないので含めない。
 */
const OPTIONS: { value: MfaPolicy; label: string; hint: string }[] = [
  { value: 'off', label: '必須にしない', hint: '各自の判断で設定します（既定）' },
  { value: 'admin', label: '管理者に必須', hint: 'ロールが管理者の人だけに要求します' },
  { value: 'all', label: '全員に必須', hint: 'MCP Connect の利用者にも要求します' },
];

export function MfaPolicySettings({
  policy,
  unenrolled,
  isAdmin,
  onSaved,
}: {
  policy: MfaPolicy;
  unenrolled: MfaUnenrolled;
  isAdmin: boolean;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<MfaPolicy>(policy);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // 再読み込みで値が入れ替わったら追随させる
  useEffect(() => setSelected(policy), [policy]);

  const affected =
    selected === 'admin' ? unenrolled.admin : selected === 'all' ? unenrolled.all : 0;

  async function save() {
    setBusy(true);
    setMessage(null);
    try {
      await api.patch('/api/tenant/settings', { mfaPolicy: selected });
      setMessage('保存しました');
      onSaved();
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5 lg:col-span-2">
      <h2 className="text-sm font-bold">二段階認証の必須化</h2>
      <p className="text-xs leading-relaxed text-ink-subtle">
        {
          '必須にすると、対象の人は認証アプリを設定するか Passkey でログインするまで、アカウント画面以外を開けなくなります。設定の手段は残るのでログインできなくなることはありません。'
        }
      </p>

      <div className="flex flex-col gap-2">
        {OPTIONS.map((option) => (
          <label key={option.value} className="flex items-start gap-3 text-sm">
            <input
              type="radio"
              name="mfaPolicy"
              className="mt-1"
              value={option.value}
              checked={selected === option.value}
              disabled={!isAdmin || busy}
              onChange={() => setSelected(option.value)}
            />
            <span className="flex flex-col gap-0.5">
              <span>{option.label}</span>
              <span className="text-xs text-ink-subtle">{option.hint}</span>
            </span>
          </label>
        ))}
      </div>

      {affected > 0 ? (
        <p className="text-xs leading-relaxed text-alert">
          {'いま設定していない人が '}
          <span className="font-mono">{affected}</span>
          {' 人います。保存すると、その人たちは次に開いたときに設定を求められます。'}
        </p>
      ) : null}

      {isAdmin ? (
        <div className="flex items-center gap-4">
          <Button variant="primary" onClick={save} disabled={busy || selected === policy}>
            保存
          </Button>
          {message ? <span className="text-xs text-ink-muted">{message}</span> : null}
        </div>
      ) : (
        <p className="text-xs text-ink-subtle">変更できるのは管理者だけです。</p>
      )}
    </section>
  );
}
